import React, { useState } from 'react'
import {
    Box,
    Title,
    Text,
    Textarea,
    Button,
    Stack,
    Card,
    Group,
    Avatar,
    Badge,
    Modal,
    LoadingOverlay,
    Alert,
    Pagination,
    ActionIcon,
    Menu,
    Divider
} from '@mantine/core'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { IconMessageCircle, IconEdit, IconTrash, IconFlag, IconDots, IconUser, IconShield } from '@tabler/icons-react'
import { commentsApi } from '../services/api'
import { Comment, CreateCommentData, UpdateCommentData } from '../types'
import { useAuth } from '@/hooks/useAuth'
import { useI18n } from '@/hooks/useI18n'
import { linkifyText } from '@/utils/linkify'
import { getGravatarUrlPreset } from '@/utils/format'

interface CommentSectionProps {
    gameSlug: string
    platformSlug: string
}

interface CommentItemProps {
    comment: Comment
    onEdit: (comment: Comment) => void
    onDelete: (comment: Comment) => void
    onMarkSpam: (comment: Comment) => void
    onUnmarkSpam: (comment: Comment) => void
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, onEdit, onDelete, onMarkSpam, onUnmarkSpam }) => {
    const { user, isAdmin } = useAuth()
    const { t } = useI18n()
    const [showMenu, setShowMenu] = useState(false)

    const canEdit = user?.id === comment.userId || isAdmin
    const canDelete = user?.id === comment.userId || isAdmin
    const canModerate = isAdmin

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString)
            const now = new Date()
            const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

            if (diffInSeconds < 60) return 'Just now'
            if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
            if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
            if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`
            if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)}mo ago`
            return `${Math.floor(diffInSeconds / 31536000)}y ago`
        } catch {
            return 'Unknown time'
        }
    }

    return (
        <Card shadow="sm" padding="md" radius="md" withBorder>
            <Group justify="space-between" align="flex-start" mb="xs">
                <Group gap="sm">
                    <Avatar
                        size="sm"
                        src={getGravatarUrlPreset(comment.user.email, 'small')}
                        alt={comment.user.email}
                    >
                        <IconUser size={16} />
                    </Avatar>
                    <div>
                        <Text size="sm" fw={500}>
                            {comment.user.email}
                            {comment.user.isAdmin && (
                                <Badge size="xs" color="red" ml="xs">
                                    Admin
                                </Badge>
                            )}
                        </Text>
                        <Text size="xs" c="dimmed">
                            {formatDate(comment.createdAt)}
                            {comment.isEdited && (
                                <Text component="span" size="xs" c="dimmed" ml="xs">
                                    (edited)
                                </Text>
                            )}
                        </Text>
                    </div>
                </Group>

                {(canEdit || canDelete || canModerate) && (
                    <Menu opened={showMenu} onChange={setShowMenu} shadow="md" width={200}>
                        <Menu.Target>
                            <ActionIcon variant="subtle" size="sm">
                                <IconDots size={16} />
                            </ActionIcon>
                        </Menu.Target>

                        <Menu.Dropdown>
                            {canEdit && (
                                <Menu.Item
                                    leftSection={<IconEdit size={14} />}
                                    onClick={() => {
                                        onEdit(comment)
                                        setShowMenu(false)
                                    }}
                                >
                                    {t('comment.edit') || 'Edit'}
                                </Menu.Item>
                            )}

                            {canDelete && (
                                <Menu.Item
                                    leftSection={<IconTrash size={14} />}
                                    color="red"
                                    onClick={() => {
                                        onDelete(comment)
                                        setShowMenu(false)
                                    }}
                                >
                                    {t('comment.delete') || 'Delete'}
                                </Menu.Item>
                            )}

                            {canModerate && (
                                <>
                                    <Divider />
                                    <Menu.Label>{t('comment.moderate') || 'Moderate'}</Menu.Label>

                                    {comment.isSpam ? (
                                        <Menu.Item
                                            leftSection={<IconShield size={14} />}
                                            onClick={() => {
                                                onUnmarkSpam(comment)
                                                setShowMenu(false)
                                            }}
                                        >
                                            {t('comment.unmarkSpam') || 'Unmark as Spam'}
                                        </Menu.Item>
                                    ) : (
                                        <Menu.Item
                                            leftSection={<IconFlag size={14} />}
                                            color="orange"
                                            onClick={() => {
                                                onMarkSpam(comment)
                                                setShowMenu(false)
                                            }}
                                        >
                                            {t('comment.markSpam') || 'Mark as Spam'}
                                        </Menu.Item>
                                    )}
                                </>
                            )}
                        </Menu.Dropdown>
                    </Menu>
                )}
            </Group>

            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {linkifyText(comment.content)}
            </Text>
        </Card>
    )
}

export const CommentSection: React.FC<CommentSectionProps> = ({ gameSlug, platformSlug }) => {
    const { isAuthenticated } = useAuth()
    const { t, country } = useI18n()
    const queryClient = useQueryClient()

    const [page, setPage] = useState(1)
    const [commentText, setCommentText] = useState('')
    const [editingComment, setEditingComment] = useState<Comment | null>(null)
    const [editText, setEditText] = useState('')
    const [showEditModal, setShowEditModal] = useState(false)
    const [showLoginAlert, setShowLoginAlert] = useState(false)
    const [commentError, setCommentError] = useState<string | null>(null)

    const limit = 10

    // Fetch comments
    const { data: commentsData, isLoading, error } = useQuery({
        queryKey: ['comments', gameSlug, platformSlug, page, country],
        queryFn: () => commentsApi.getComments(gameSlug, platformSlug, { page, limit, country }),
        enabled: !!gameSlug && !!platformSlug,
    })

    // Create comment mutation
    const createCommentMutation = useMutation({
        mutationFn: (data: CreateCommentData) => commentsApi.createComment(gameSlug, platformSlug, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comments', gameSlug, platformSlug] })
            setCommentText('')
            setCommentError(null)
        },
        onError: (error: any) => {
            // Try to extract backend error message
            let message = t('comment.errorGeneric') || 'Failed to post comment.';
            const censoredWords = error?.response?.data?.censoredWords;
            if (Array.isArray(censoredWords) && censoredWords.length > 0) {
                message = t('comment.censoredWordsError', { words: censoredWords.join(', ') }) ||
                    `You cannot use the following words: ${censoredWords.join(', ')}`;
            } else if (error?.response?.data?.error) {
                message = error.response.data.error;
            }
            setCommentError(message);
        },
    })

    // Update comment mutation
    const updateCommentMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: UpdateCommentData }) =>
            commentsApi.updateComment(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comments', gameSlug, platformSlug] })
            setShowEditModal(false)
            setEditingComment(null)
            setEditText('')
        },
    })

    // Delete comment mutation
    const deleteCommentMutation = useMutation({
        mutationFn: (id: string) => commentsApi.deleteComment(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comments', gameSlug, platformSlug] })
        },
    })

    // Mark as spam mutation
    const markSpamMutation = useMutation({
        mutationFn: (id: string) => commentsApi.markAsSpam(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comments', gameSlug, platformSlug] })
        },
    })

    // Unmark as spam mutation
    const unmarkSpamMutation = useMutation({
        mutationFn: (id: string) => commentsApi.unmarkAsSpam(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comments', gameSlug, platformSlug] })
        },
    })

    const handleSubmitComment = () => {
        if (!isAuthenticated) {
            setShowLoginAlert(true)
            return
        }

        if (!commentText.trim()) return

        createCommentMutation.mutate({
            content: commentText.trim(),
            country: country,
        })
    }

    const handleEditComment = (comment: Comment) => {
        setEditingComment(comment)
        setEditText(comment.content)
        setShowEditModal(true)
    }

    const handleUpdateComment = () => {
        if (!editingComment || !editText.trim()) return

        updateCommentMutation.mutate({
            id: editingComment.id,
            data: { content: editText.trim() }
        })
    }

    const handleDeleteComment = (comment: Comment) => {
        if (confirm(t('comment.confirmDelete') || 'Are you sure you want to delete this comment?')) {
            deleteCommentMutation.mutate(comment.id)
        }
    }

    const handleMarkSpam = (comment: Comment) => {
        if (confirm(t('comment.confirmMarkSpam') || 'Are you sure you want to mark this comment as spam?')) {
            markSpamMutation.mutate(comment.id)
        }
    }

    const handleUnmarkSpam = (comment: Comment) => {
        unmarkSpamMutation.mutate(comment.id)
    }

    const comments = commentsData?.data?.data || []
    const totalPages = commentsData?.data?.pagination?.totalPages || 0
    const totalComments = commentsData?.data?.pagination?.total || 0

    return (
        <Box>
            <Title order={3} mb="md" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconMessageCircle size={20} />
                {t('comment.title') || 'Comments'} ({totalComments})
            </Title>

            {/* Comment Form */}
            <Card shadow="sm" padding="md" radius="md" withBorder mb="lg">
                {commentError && (
                    <Alert color="red" mb="md" onClose={() => setCommentError(null)}>
                        {commentError}
                    </Alert>
                )}
                <Textarea
                    placeholder={isAuthenticated
                        ? (t('comment.placeholder') || 'Share your thoughts about this game...')
                        : (t('comment.loginRequired') || 'Please log in to comment')
                    }
                    value={commentText}
                    onChange={(e) => {
                        setCommentText(e.target.value)
                        if (commentError) setCommentError(null)
                    }}
                    minRows={3}
                    maxRows={6}
                    disabled={!isAuthenticated}
                    mb="md"
                />

                <Group justify="space-between">
                    <Text size="xs" c="dimmed">
                        {commentText.length}/1000
                    </Text>
                    <Button
                        onClick={handleSubmitComment}
                        disabled={!isAuthenticated || !commentText.trim() || createCommentMutation.isPending}
                        loading={createCommentMutation.isPending}
                        color="customGreen"
                        variant="light"
                    >
                        {t('comment.post') || 'Post Comment'}
                    </Button>
                </Group>

                {showLoginAlert && (
                    <Alert color="blue" mt="md" onClose={() => setShowLoginAlert(false)}>
                        {t('comment.loginToComment') || 'Please log in to post a comment.'}
                    </Alert>
                )}
            </Card>

            {/* Comments List */}
            <Box pos="relative">
                <LoadingOverlay visible={isLoading} />

                {error && (
                    <Alert color="red" mb="md">
                        {t('comment.errorLoading') || 'Error loading comments. Please try again.'}
                    </Alert>
                )}

                {comments.length === 0 && !isLoading ? (
                    <Card shadow="sm" padding="xl" radius="md" withBorder>
                        <Text ta="center" c="dimmed">
                            {t('comment.noComments') || 'No comments yet. Be the first to share your thoughts!'}
                        </Text>
                    </Card>
                ) : (
                    <Stack gap="md">
                        {comments.map((comment: Comment) => (
                            <CommentItem
                                key={comment.id}
                                comment={comment}
                                onEdit={handleEditComment}
                                onDelete={handleDeleteComment}
                                onMarkSpam={handleMarkSpam}
                                onUnmarkSpam={handleUnmarkSpam}
                            />
                        ))}
                    </Stack>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <Group justify="center" mt="xl">
                        <Pagination
                            value={page}
                            onChange={setPage}
                            total={totalPages}
                            size="sm"
                        />
                    </Group>
                )}
            </Box>

            {/* Edit Comment Modal */}
            <Modal
                opened={showEditModal}
                onClose={() => setShowEditModal(false)}
                title={t('comment.editComment') || 'Edit Comment'}
                size="md"
            >
                <Stack gap="md">
                    <Textarea
                        placeholder={t('comment.editPlaceholder') || 'Edit your comment...'}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        minRows={3}
                        maxRows={6}
                        mb="md"
                    />

                    {/* Preview */}
                    {editText.trim() && (
                        <Box>
                            <Text size="sm" fw={500} mb="xs">
                                {t('comment.preview') || 'Preview:'}
                            </Text>
                            <Card withBorder p="sm" bg="gray.0">
                                <Text size="sm">
                                    {linkifyText(editText)}
                                </Text>
                            </Card>
                        </Box>
                    )}

                    <Group justify="space-between">
                        <Text size="xs" c="dimmed">
                            {editText.length}/1000
                        </Text>
                        <Group gap="sm">
                            <Button
                                variant="subtle"
                                onClick={() => setShowEditModal(false)}
                            >
                                {t('common.cancel') || 'Cancel'}
                            </Button>
                            <Button
                                onClick={handleUpdateComment}
                                disabled={!editText.trim() || updateCommentMutation.isPending}
                                loading={updateCommentMutation.isPending}
                                color="customGreen"
                            >
                                {t('comment.update') || 'Update Comment'}
                            </Button>
                        </Group>
                    </Group>
                </Stack>
            </Modal>
        </Box>
    )
} 